/**
 * ReCAPTCHA loader utility for Vite
 * Dynamically loads reCAPTCHA with the site key from environment variables
 */

let recaptchaLoaded = false;
let loadPromise = null;

export const loadRecaptcha = () => {
  // Return existing promise if already loading
  if (loadPromise) {
    return loadPromise;
  }

  // Return resolved promise if already loaded
  if (recaptchaLoaded) {
    return Promise.resolve();
  }

  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  if (!siteKey) {
    console.warn('ReCAPTCHA site key not configured');
    return Promise.resolve();
  }

  loadPromise = new Promise((resolve, reject) => {
    // Check if script already exists
    const existingScript = document.querySelector('script[src*="recaptcha"]');
    if (existingScript) {
      recaptchaLoaded = true;
      resolve();
      return;
    }

    // Create and load the script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      recaptchaLoaded = true;
      console.log('ReCAPTCHA loaded successfully');
      resolve();
    };

    script.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
      reject(new Error('Failed to load reCAPTCHA'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
};

// Execute reCAPTCHA action
export const executeRecaptcha = async (action) => {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  if (!siteKey) {
    console.warn('ReCAPTCHA site key not configured');
    return null;
  }

  // Ensure reCAPTCHA is loaded
  await loadRecaptcha();

  // Wait for grecaptcha to be available
  if (typeof window.grecaptcha === 'undefined') {
    console.error('grecaptcha is not available');
    return null;
  }

  try {
    const token = await window.grecaptcha.enterprise.execute(siteKey, { action });
    return token;
  } catch (error) {
    console.error('Error executing reCAPTCHA:', error);
    return null;
  }
};